<template>
  <div>
    <Head :title="`${form.first_name} ${form.last_name}`" />
    <div class="grid grid-cols-5 gap-4">
      <div class="col-span-5 lg:col-span-3">
        <div class="flex justify-start mb-8">
          <h1 class="text-3xl font-bold">
            <Link class="text-indigo-400 hover:text-indigo-600" href="/users">Users </Link>
            <span class="text-indigo-400 font-medium">/</span>
            {{ form.first_name }} {{ form.last_name }}
          </h1>
          <img v-if="user.photo" class="block ml-4 w-8 h-8 rounded-full" :src="user.photo" />
        </div>
        <trashed-message v-if="user.deleted_at" class="mb-6" @restore="restore"> This user has been deleted. </trashed-message>
        <div class="bg-white rounded-md shadow overflow-hidden">
          <form @submit.prevent="update">
            <div class="flex flex-wrap -mb-8 -mr-6 p-8">
              <text-input v-model="form.first_name" :error="form.errors.first_name" class="pb-8 pr-6 w-full lg:w-1/2" label="First name" name="first_name" @blur="update" />
              <text-input v-model="form.last_name" :error="form.errors.last_name" class="pb-8 pr-6 w-full lg:w-1/2" label="Last name" name="last_name" @blur="update" />
              <text-input v-model="form.email" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/2" label="Email" name="email" @blur="update" />
              <text-input v-model="form.password" :error="form.errors.password" class="pb-8 pr-6 w-full lg:w-1/2" type="password" autocomplete="new-password" label="Password" name="password" @blur="update" />
              <phone-input v-model.phoneFormat="form.phone" :type="tel" :error="form.errors.phone" class="relative pr-6 pb-8 w-full lg:w-1/2" label="Phone" name="phone" @blur="update" />
              <text-input v-model="form.nmls" :error="form.errors.nmls" class="pb-8 pr-6 w-full lg:w-1/2" label="NMLS ID" name="nmls" @blur="update" />
              <text-input v-model="form.appointment_link" :error="form.errors.appointment_link" class="pr-6 pb-8 w-full" label="Appointment Link" name="appointment_link" @blur="update" />
              <text-input v-model="form.application_link" :error="form.errors.application_link" class="pr-6 pb-8 w-full" label="Application Link" name="application_link" @blur="update" />
              <text-input v-model="form.application_api" :error="form.errors.application_api" class="pb-8 pr-6 w-full lg:w-1/2" label="Application API" name="application_api" @blur="update" />
              <file-input v-model="form.photo" :error="form.errors.photo" class="pb-8 pr-6 w-full lg:w-1/2" type="file" accept="image/*" label="Photo" name="photo" @change="update" />
              <select-input v-model="form.role" :error="form.errors.role" class="pb-8 pr-6 w-full lg:w-1/2" label="Role" name="role" @change="update">
                <option value="LoanOfficer">LoanOfficer</option>
                <option value="Processor">Processor</option>
                <option value="Staff">Staff</option>
              </select-input>
              <select-input v-if="user.owner" v-model="form.owner" :error="form.errors.owner" class="pb-8 pr-6 w-full lg:w-1/2" label="Owner" name="owner" @change="update">
                <option :value="true">Yes</option>
                <option :value="false">No</option>
              </select-input>
              <div class="lg:w-1/2">
                <a href="https://platform.ringcentral.com/restapi/oauth/authorize?response_type=code&redirect_uri=https://nowsauce.com/oauth/callback&client_id=r9OdZlJlT_SQxBIcLEdaNQ">
                  <span class="btn-indigo ml-auto mt-6">Connect Ringcentral</span>
                </a>
              </div>
              <!-- <text-input v-model="form.phone_pass" :error="form.errors.phone_pass" class="pb-8 pr-6 w-full lg:w-1/2" type="password" label="Ringcentral Pass" /> -->
              <text-input v-model="form.phone_extension" :error="form.errors.phone_extension" class="pb-8 pr-6 w-full lg:w-1/2" label="Ringcentral Extension" name="phone_extension" @blur="update" />
              <div class="w-full pr-6 pb-8">
                <SignatureEditor v-model="form.signature" />
                <div class="text-right font-xs mt-2">
                  <span @click="makeSignature">Click here to Generate Signature</span>
                </div>
              </div>
            </div>
            <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
              <button v-if="!user.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">Delete User</button>
              <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">Update User</loading-button>
            </div>
          </form>
        </div>
      </div>
      <div class="col-span-5 lg:col-span-2 min-w-2xl">
        <AccessTokens :user="user" :tokens="tokens" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link, router } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import SignatureEditor from '@/Shared/SignatureEditor'
import PhoneInput from '@/Shared/PhoneInput'
import AccessTokens from '@/Shared/AccessTokens'

export default {
  components: {
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
    SignatureEditor,
    PhoneInput,
    AccessTokens,
  },
  layout: Layout,
  props: {
    user: Object,
    tokens: Object,
  },
  remember: 'form',
  data() {
    return {
      form: router.form({
        _method: 'put',
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
        password: '',
        owner: this.user.owner,
        signature: this.user.signature,
        phone: this.user.phone,
        role: this.user.role,
        nmls: this.user.nmls,
        application_api: this.user.application_api,
        application_link: this.user.application_link,
        appointment_link: this.user.appointment_link,
        photo: null,
        phone_pass: this.user.phone_pass,
        phone_extension: this.user.phone_extension,
      }),
      originalValues: {},
    }
  },
  created() {
    this.originalValues = {
      first_name: this.user.first_name,
      last_name: this.user.last_name,
      email: this.user.email,
      password: '',
      owner: this.user.owner,
      signature: this.user.signature,
      phone: this.user.phone,
      role: this.user.role,
      nmls: this.user.nmls,
      application_api: this.user.application_api,
      application_link: this.user.application_link,
      appointment_link: this.user.appointment_link,
      photo: null,
      phone_pass: this.user.phone_pass,
      phone_extension: this.user.phone_extension,
    }
  },
  methods: {
    hasChanged(field) {
      if (field === 'owner') {
        const originalValue = this.originalValues.owner === true || this.originalValues.owner === 'true'
        const currentValue = this.form.owner === true || this.form.owner === 'true'
        return originalValue !== currentValue
      }
      
      if (field === 'photo') {
        return this.form[field] !== null
      }

      return this.form[field] !== this.originalValues[field]
    },
    update() {
      const changedField = event?.target?.name

      if (changedField && !this.hasChanged(changedField)) {
        return
      }

      this.form.owner = this.form.owner === 'true' || this.form.owner === true ? true : false
      
      this.form.post(`/users/${this.user.id}`, {
        preserveScroll: true,
        onSuccess: () => {
          if (this.form.password) {
            this.form.reset('password')
          } else if (this.form.photo) {
            this.form.reset('photo')
          } else {
            this.originalValues[changedField] = this.form[changedField]
          }
        },
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this user?')) {
        router.delete(`/users/${this.user.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this user?')) {
        router.put(`/users/${this.user.id}/restore`)
      }
    },
    updateSignature: function(signature){
      this.form.signature=signature
    },
    makeSignature(){
      this.form.signature = this.user.signature_alternate
    }
  },
}
</script>
